"use client";

import { c as _c } from "react/compiler-runtime";
import useCapacitor from "@/hooks/useCapacitor";
import { faCamera } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { toPng } from "html-to-image";
import Link from "next/link";
import { memo, useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import type { DisplayTz, SeasonMintScanResult } from "./meme-calendar.helpers";
import { displayedSeasonNumberFromIndex, formatFullDate, formatFullDateTime, formatToFullDivision, getCanonicalNextMintNumber, getMintNumberForMintDate, getMintTimelineDetails, getNextMintStart, getUpcomingMintsForCurrentOrNextSeason, getUpcomingMintsForSeasonIndex, printCalendarInvites, ymd } from "./meme-calendar.helpers";

/**
 * Layout wrapper: global Local/UTC toggle + two cards
 */
interface MemeCalendarOverviewProps {
  readonly displayTz: DisplayTz;
  readonly showViewAll?: boolean | undefined;
}
export default function MemeCalendarOverview(t0) {
  const $ = _c(15);
  const {
    displayTz,
    showViewAll: t1
  } = t0;
  const showViewAll = t1 === undefined ? false : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <h1 className="tw-mb-0">The Memes Minting Calendar</h1>;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== showViewAll) {
    t3 = showViewAll && <Link href="/meme-calendar"><span className="tw-whitespace-nowrap tw-text-sm tw-font-medium hover:tw-text-[#bbb] max-[800px]:tw-text-[12px]">View Full Calendar</span></Link>;
    $[1] = showViewAll;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== t3) {
    t4 = <div className="tw-h-full tw-flex tw-items-center tw-gap-3">{t2}{t3}</div>;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== displayTz) {
    t5 = <div className="tw-h-full"><MemeCalendarOverviewNextMint displayTz={displayTz} /></div>;
    $[5] = displayTz;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== displayTz) {
    t6 = <div className="tw-h-full"><MemeCalendarOverviewUpcomingMints displayTz={displayTz} /></div>;
    $[7] = displayTz;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t5 || $[10] !== t6) {
    t7 = <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">{t5}{t6}</div>;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t4 || $[13] !== t7) {
    t8 = <div className="tw-flex tw-flex-col tw-gap-3">{t4}{t7}</div>;
    $[12] = t4;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  return t8;
}

/**
 * Card 1 — Next mint: big number, date (single line governed by global toggle),
 * live countdown, calendar links.
 */
interface MemeCalendarOverviewNextMintProps {
  readonly displayTz: DisplayTz;
  readonly id?: number | undefined;
}
const TopControls = memo(function TopControls(props) {
  const $ = _c(23);
  const {
    isCapacitor
  } = useCapacitor();
  const {
    canonicalNextMintNumber,
    selectedMintNumber,
    onSelect,
    mintInputRef,
    onMintInputChange,
    onMintInputSubmit,
    onScreenshot,
    isCapturing
  } = props;
  const t0 = canonicalNextMintNumber === selectedMintNumber;
  let t1;
  if ($[0] !== canonicalNextMintNumber || $[1] !== onSelect) {
    t1 = () => onSelect(canonicalNextMintNumber);
    $[0] = canonicalNextMintNumber;
    $[1] = onSelect;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t0 || $[4] !== t1) {
    t2 = <button disabled={t0} type="button" className="tw-inline-flex tw-items-center tw-justify-center tw-h-8 tw-rounded-md tw-bg-white tw-text-black tw-px-3 tw-text-sm tw-font-semibold tw-border tw-border-[#d1d1d1] hover:tw-bg-[#e9e9e9] disabled:tw-opacity-75 disabled:hover:tw-bg-white disabled:hover:tw-text-black disabled:hover:tw-border-[#d1d1d1]" onClick={t1}>Next Mint</button>;
    $[3] = t0;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="tw-shrink-0 tw-select-none tw-pr-2">Meme #</div>;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== onMintInputChange) {
    t4 = event => {
      const v = event.target.value.replace(/\D/g, "");
      onMintInputChange(v);
    };
    $[7] = onMintInputChange;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== mintInputRef || $[10] !== t4) {
    t5 = <div className="tw-bg-[#e5e5e5] tw-h-8 tw-flex tw-items-center tw-rounded-md tw-text-black tw-font-semibold tw-pl-3 tw-border tw-border-[#d1d1d1]">{t3}<input id="meme-overview-mint-input" ref={mintInputRef} type="number" min={1} name="meme-overview-mint-input" placeholder="123" onChange={t4} className="tw-text-black placeholder:tw-text-gray-500 focus:tw-outline-none tw-border-none tw-h-8 tw-w-[8ch] tw-px-2 tw-rounded-r-md" /></div>;
    $[9] = mintInputRef;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== onMintInputSubmit || $[13] !== t5) {
    t6 = <form onSubmit={onMintInputSubmit}>{t5}</form>;
    $[12] = onMintInputSubmit;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== isCapacitor || $[16] !== isCapturing || $[17] !== onScreenshot) {
    t7 = !isCapacitor && <><div className="tw-flex-1" /><ScreenshotCard onScreenshot={onScreenshot} isCapturing={isCapturing} /></>;
    $[15] = isCapacitor;
    $[16] = isCapturing;
    $[17] = onScreenshot;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== t2 || $[20] !== t6 || $[21] !== t7) {
    t8 = <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-mb-3" data-ignore-screenshot={true}>{t2}{t6}{t7}</div>;
    $[19] = t2;
    $[20] = t6;
    $[21] = t7;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  return t8;
});
export function MemeCalendarOverviewNextMint({
  displayTz,
  id
}: MemeCalendarOverviewNextMintProps) {
  const [now, setNow] = useState(new Date());
  const [isManualSelection, setIsManualSelection] = useState(false);
  const [selectedMintNumber, setSelectedMintNumber] = useState(() => {
    if (id != null) return id;
    const upcomingInstant = getNextMintStart(new Date());
    const upcomingUtcDay = new Date(Date.UTC(upcomingInstant.getUTCFullYear(), upcomingInstant.getUTCMonth(), upcomingInstant.getUTCDate()));
    return getMintNumberForMintDate(upcomingUtcDay);
  });
  const [mintInputValue, setMintInputValue] = useState("");
  const [isCapturing, setIsCapturing] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const mintInputRef = useRef<HTMLInputElement>(null);
  const canonicalNextMintNumber = useMemo(() => getCanonicalNextMintNumber(now), [now]);
  const handleMintSelection = useCallback((mintNumber: number) => {
    setSelectedMintNumber(mintNumber);
    setIsManualSelection(mintNumber !== canonicalNextMintNumber);
  }, [canonicalNextMintNumber]);
  const handleMintInputChange = useCallback((v: string) => {
    setMintInputValue(v);
  }, []);
  const handleMintInputSubmit = useCallback((event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const parsed = parseInt(mintInputValue, 10);
    if (Number.isNaN(parsed) || parsed < 1) {
      return;
    }
    handleMintSelection(parsed);
    setMintInputValue("");
    mintInputRef.current?.blur();
  }, [mintInputValue, handleMintSelection]);
  useEffect(() => {
    const tick = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(tick);
  }, []);
  useEffect(() => {
    if (id != null) {
      setSelectedMintNumber(id);
      setIsManualSelection(true);
    }
  }, [id]);
  useEffect(() => {
    if (id == null && !isManualSelection && selectedMintNumber !== canonicalNextMintNumber) {
      setSelectedMintNumber(canonicalNextMintNumber);
    }
  }, [id, canonicalNextMintNumber, isManualSelection, selectedMintNumber]);
  useEffect(() => {
    if (id == null && isManualSelection && selectedMintNumber === canonicalNextMintNumber) {
      setIsManualSelection(false);
    }
  }, [id, canonicalNextMintNumber, isManualSelection, selectedMintNumber]);
  const mintDetails = useMemo(() => getMintTimelineDetails(selectedMintNumber), [selectedMintNumber]);
  const invitesHtml = useMemo(() => printCalendarInvites(mintDetails.instantUtc, mintDetails.mintNumber), [mintDetails]);
  const nowMs = now.getTime();
  const startMs = mintDetails.instantUtc.getTime();
  const endMs = mintDetails.mintEndUtc.getTime();
  const isUpcoming = nowMs < startMs;
  const isPast = nowMs >= endMs;
  let heading: string;
  if (isUpcoming) {
    heading = mintDetails.mintNumber === canonicalNextMintNumber ? "Next Mint" : "Upcoming Mint";
  } else if (isPast) {
    heading = "Past Mint";
  } else {
    heading = "Mint Live";
  }
  let countdownTitle = "Minting in";
  let countdownSuffix: string | null = null;
  let countdownParts: ReturnType<typeof msToParts>;
  if (isUpcoming) {
    countdownParts = msToParts(startMs - nowMs);
  } else if (isPast) {
    countdownTitle = "Minted";
    countdownSuffix = "ago";
    countdownParts = msToParts(nowMs - startMs);
  } else {
    countdownTitle = "Mint ends in";
    countdownParts = msToParts(endMs - nowMs);
  }
  const countdownText = formatDurationParts(countdownParts);
  const finalCountdown = countdownSuffix ? <>
      {countdownText}{" "}
      <span className="tw-text-sm tw-font-normal">{countdownSuffix}</span>
    </> : countdownText;
  const handleScreenshot = useCallback(async () => {
    if (!cardRef.current) return;
    try {
      setIsCapturing(true);

      // Clone the card and strip ignored elements
      const clone = cardRef.current.cloneNode(true) as HTMLElement;
      clone.querySelectorAll("[data-ignore-screenshot]").forEach(el => el.remove());

      // Create a tight, offscreen wrapper so there is no outside gap
      const mount = document.createElement("div");
      const rect = cardRef.current.getBoundingClientRect();
      Object.assign(mount.style, {
        position: "fixed",
        left: "-10000px",
        top: "0",
        padding: "0",
        margin: "0",
        background: "transparent",
        display: "inline-block",
        lineHeight: "normal",
        width: `${rect.width}px` // lock width to avoid reflow differences
      } as CSSStyleDeclaration);

      // Ensure the clone itself doesn't carry margins
      Object.assign(clone.style, {
        margin: "0",
        height: "auto",
        minHeight: "0",
        display: "block"
      } as CSSStyleDeclaration);
      mount.appendChild(clone);
      document.body.appendChild(mount);

      // Measure tight dimensions and render the clone directly
      const width = Math.ceil(clone.scrollWidth || rect.width);
      const height = Math.ceil(clone.scrollHeight || clone.getBoundingClientRect().height || 0);
      const dataUrl = await toPng(clone, {
        cacheBust: true,
        pixelRatio: window.devicePixelRatio || 1,
        width,
        height,
        backgroundColor: "#0c0c0d" // solid card bg so PNG doesn't look empty
      });
      const link = document.createElement("a");
      link.href = dataUrl;
      link.download = `meme-${mintDetails.mintNumber}-mint.png`;
      link.click();
      document.body.removeChild(mount);
    } catch (error) {
      console.error("Failed to capture meme calendar panel", error);
    } finally {
      setIsCapturing(false);
    }
  }, [mintDetails]);
  return <div className="tw-relative">
      <div ref={cardRef} className="tw-p-4 tw-flex tw-flex-col tw-justify-between tw-bg-[#0c0c0d] tw-rounded-md tw-border tw-border-solid tw-border-[#222222]">
        <div className="tw-space-y-1">
          {id == null && <TopControls canonicalNextMintNumber={canonicalNextMintNumber} selectedMintNumber={selectedMintNumber} onSelect={handleMintSelection} mintInputRef={mintInputRef} onMintInputChange={handleMintInputChange} onMintInputSubmit={handleMintInputSubmit} onScreenshot={handleScreenshot} isCapturing={isCapturing} />}
          <div className="tw-flex tw-items-center tw-gap-2 tw-justify-between">
            <div className="tw-text-sm tw-text-gray-400">{heading}</div>
            {id != null && <ScreenshotCard onScreenshot={handleScreenshot} isCapturing={isCapturing} />}
          </div>
          <div className="tw-flex tw-items-center tw-gap-2">
            <div className="!tw-text-3xl md:!tw-text-4xl tw-font-bold">
              #{mintDetails.mintNumber.toLocaleString()}
            </div>
          </div>
          <div className="tw-text-lg tw-font-semibold">
            {formatFullDateTime(mintDetails.instantUtc, displayTz)}
          </div>
          <div className="tw-text-sm">
            {formatToFullDivision(mintDetails.instantUtc)}
          </div>

          <div className="tw-pt-2 tw-text-sm tw-text-gray-400">
            {countdownTitle}
          </div>
          <div className="tw-text-2xl tw-font-semibold">{finalCountdown}</div>
        </div>

        <div data-ignore-screenshot className="tw-pt-3" dangerouslySetInnerHTML={{
        __html: invitesHtml
      }} />
      </div>
    </div>;
}

/**
 * Card 2 — Upcoming mints for the current SZN.
 * Shows a table of remaining Mon/Wed/Fri date-times (timed, not all-day).
 */
interface MemeCalendarOverviewUpcomingMintsProps {
  readonly displayTz: DisplayTz;
}
function MemeCalendarOverviewUpcomingMints(t0) {
  const $ = _c(53);
  const {
    displayTz
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Date();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [now] = useState(t1);
  let t2;
  if ($[1] !== now) {
    t2 = getUpcomingMintsForCurrentOrNextSeason(now);
    $[1] = now;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const currentSeason = t2;
  let t3;
  if ($[3] !== now) {
    t3 = getCanonicalNextMintNumber(now);
    $[3] = now;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const canonicalNextMintNumber = t3;
  let t4;
  bb0: {
    let t5;
    if ($[5] !== canonicalNextMintNumber || $[6] !== currentSeason.rows) {
      let t6;
      if ($[8] !== canonicalNextMintNumber) {
        t6 = row => row.meme === canonicalNextMintNumber;
        $[8] = canonicalNextMintNumber;
        $[9] = t6;
      } else {
        t6 = $[9];
      }
      t5 = currentSeason.rows.some(t6);
      $[5] = canonicalNextMintNumber;
      $[6] = currentSeason.rows;
      $[7] = t5;
    } else {
      t5 = $[7];
    }
    const containsCanonical = t5;
    let t6;
    if ($[10] !== canonicalNextMintNumber || $[11] !== containsCanonical || $[12] !== currentSeason.rows) {
      t6 = containsCanonical ? currentSeason.rows.filter(row_0 => row_0.meme !== canonicalNextMintNumber) : currentSeason.rows;
      $[10] = canonicalNextMintNumber;
      $[11] = containsCanonical;
      $[12] = currentSeason.rows;
      $[13] = t6;
    } else {
      t6 = $[13];
    }
    const filtered = t6;
    if (filtered.length === 0 && containsCanonical) {
      const t7 = currentSeason.seasonIndex + 1;
      let t8;
      if ($[14] !== now || $[15] !== t7) {
        t8 = getUpcomingMintsForSeasonIndex(t7, now);
        $[14] = now;
        $[15] = t7;
        $[16] = t8;
      } else {
        t8 = $[16];
      }
      const nextSeason = t8;
      let t9;
      if ($[17] !== nextSeason.rows || $[18] !== nextSeason.seasonEndInclusive || $[19] !== nextSeason.seasonIndex || $[20] !== nextSeason.seasonStart) {
        t9 = {
          seasonStart: nextSeason.seasonStart,
          seasonEndInclusive: nextSeason.seasonEndInclusive,
          seasonIndex: nextSeason.seasonIndex,
          filteredRows: nextSeason.rows,
          isNextSeason: true
        };
        $[17] = nextSeason.rows;
        $[18] = nextSeason.seasonEndInclusive;
        $[19] = nextSeason.seasonIndex;
        $[20] = nextSeason.seasonStart;
        $[21] = t9;
      } else {
        t9 = $[21];
      }
      t4 = t9;
      break bb0;
    }
    let t7;
    if ($[22] !== currentSeason.seasonEndInclusive || $[23] !== currentSeason.seasonIndex || $[24] !== currentSeason.seasonStart || $[25] !== filtered) {
      t7 = {
        seasonStart: currentSeason.seasonStart,
        seasonEndInclusive: currentSeason.seasonEndInclusive,
        seasonIndex: currentSeason.seasonIndex,
        filteredRows: filtered,
        isNextSeason: false
      };
      $[22] = currentSeason.seasonEndInclusive;
      $[23] = currentSeason.seasonIndex;
      $[24] = currentSeason.seasonStart;
      $[25] = filtered;
      $[26] = t7;
    } else {
      t7 = $[26];
    }
    t4 = t7;
  }
  const {
    seasonStart,
    seasonEndInclusive,
    seasonIndex,
    filteredRows,
    isNextSeason
  } = t4;
  const t5 = isNextSeason ? "Upcoming SZN" : "Upcoming Mints for SZN";
  let t6;
  if ($[27] !== seasonIndex) {
    t6 = displayedSeasonNumberFromIndex(seasonIndex);
    $[27] = seasonIndex;
    $[28] = t6;
  } else {
    t6 = $[28];
  }
  let t7;
  if ($[29] !== t5 || $[30] !== t6) {
    t7 = <div className="tw-font-semibold">{t5}{" "}{t6}</div>;
    $[29] = t5;
    $[30] = t6;
    $[31] = t7;
  } else {
    t7 = $[31];
  }
  let t8;
  if ($[32] !== displayTz || $[33] !== seasonStart) {
    t8 = formatFullDate(seasonStart, displayTz);
    $[32] = displayTz;
    $[33] = seasonStart;
    $[34] = t8;
  } else {
    t8 = $[34];
  }
  let t9;
  if ($[35] !== displayTz || $[36] !== seasonEndInclusive) {
    t9 = formatFullDate(seasonEndInclusive, displayTz);
    $[35] = displayTz;
    $[36] = seasonEndInclusive;
    $[37] = t9;
  } else {
    t9 = $[37];
  }
  let t10;
  if ($[38] !== t8 || $[39] !== t9) {
    t10 = <div className="tw-text-sm tw-text-gray-400">{t8} -{" "}{t9}</div>;
    $[38] = t8;
    $[39] = t9;
    $[40] = t10;
  } else {
    t10 = $[40];
  }
  let t11;
  if ($[41] !== t10 || $[42] !== t7) {
    t11 = <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">{t7}{t10}</div>;
    $[41] = t10;
    $[42] = t7;
    $[43] = t11;
  } else {
    t11 = $[43];
  }
  let t12;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <thead />;
    $[44] = t12;
  } else {
    t12 = $[44];
  }
  let t13;
  if ($[45] !== displayTz || $[46] !== filteredRows) {
    t13 = filteredRows.length === 0 ? <tr><td className="tw-py-3 tw-text-gray-500" colSpan={3}>{"No upcoming mints in this season."}</td></tr> : filteredRows.map(t14 => {
      const {
        utcDay,
        instantUtc,
        meme
      } = t14;
      return <tr key={ymd(utcDay)}><td className="tw-py-2 tw-font-semibold">#{meme.toLocaleString()}</td><td className="tw-py-2 tw-pr-4">{formatFullDateTime(instantUtc, displayTz)}</td><td className="tw-py-2 tw-flex tw-items-center tw-justify-end tw-pr-6" dangerouslySetInnerHTML={{
          __html: printCalendarInvites(instantUtc, meme, "#fff", 18)
        }} /></tr>;
    });
    $[45] = displayTz;
    $[46] = filteredRows;
    $[47] = t13;
  } else {
    t13 = $[47];
  }
  let t14;
  if ($[48] !== t13) {
    t14 = <div className="tw-overflow-x-auto tw-flex-1 tw-max-h-[390px] tw-overflow-y-auto tw-scrollbar-thin tw-scrollbar-thumb-iron-500 tw-scrollbar-track-iron-800 tw-transition-colors tw-duration-500 desktop-hover:hover:tw-scrollbar-thumb-iron-300"><table className="tw-w-full tw-text-sm">{t12}<tbody>{t13}</tbody></table></div>;
    $[48] = t13;
    $[49] = t14;
  } else {
    t14 = $[49];
  }
  let t15;
  if ($[50] !== t11 || $[51] !== t14) {
    t15 = <div className="tw-h-full tw-p-4 tw-flex tw-flex-col tw-bg-[#0c0c0d] tw-rounded-md tw-border tw-border-solid tw-border-[#222222]">{t11}{t14}</div>;
    $[50] = t11;
    $[51] = t14;
    $[52] = t15;
  } else {
    t15 = $[52];
  }
  return t15;
}

/* ---------------- helpers ---------------- */

function msToParts(ms: number) {
  const s = Math.floor(ms / 1000);
  const d = Math.floor(s / 86400);
  const h = Math.floor(s % 86400 / 3600);
  const m = Math.floor(s % 3600 / 60);
  const sec = s % 60;
  return {
    d,
    h,
    m,
    s: sec
  };
}
type DurationParts = ReturnType<typeof msToParts>;
function formatDurationParts(parts: DurationParts): string {
  const segments: string[] = [];
  if (parts.d > 0) {
    segments.push(`${parts.d.toLocaleString()}d`, `${parts.h}h`, `${parts.m}m`, `${parts.s}s`);
  } else if (parts.h > 0) {
    segments.push(`${parts.h}h`, `${parts.m}m`, `${parts.s}s`);
  } else if (parts.m > 0) {
    segments.push(`${parts.m}m`, `${parts.s}s`);
  } else {
    segments.push(`${parts.s}s`);
  }
  return segments.join(" : ");
}
function ScreenshotCard(t0) {
  const $ = _c(4);
  const {
    onScreenshot,
    isCapturing
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <FontAwesomeIcon icon={faCamera} className="tw-h-4 tw-w-4" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== isCapturing || $[2] !== onScreenshot) {
    t2 = <button data-ignore-screenshot={true} type="button" onClick={onScreenshot} disabled={isCapturing} className="tw-inline-flex tw-items-center tw-justify-center tw-h-8 tw-w-8 tw-rounded-md tw-bg-white tw-text-black tw-transition hover:tw-bg-[#e9e9e9] focus:tw-outline-none disabled:tw-opacity-50 tw-border tw-border-[#d1d1d1]" aria-label="Screenshot" title="Screenshot">{t1}</button>;
    $[1] = isCapturing;
    $[2] = onScreenshot;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}